import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { adminApi, type DailyCount, type UsageStats } from '../../api/admin'
import { Activity, Users, FileCheck, Bot, AlertCircle, Calendar, Download, Filter, Clock, Globe } from 'lucide-react'

type Preset = '1d' | '7d' | '30d' | 'custom'

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function presetRange(preset: Exclude<Preset, 'custom'>): { start: string; end: string } {
  const end = new Date()
  const start = new Date()
  const offset = preset === '1d' ? 0 : preset === '7d' ? 6 : 29
  start.setDate(start.getDate() - offset)
  return { start: isoDay(start), end: isoDay(end) }
}

function fmtDuration(sec: number | null): string {
  if (sec == null) return '—'
  if (sec < 60) return `${sec.toFixed(0)}s`
  return `${(sec / 60).toFixed(1)}m`
}

function fmtMinutes(min: number | null): string {
  if (min == null) return '—'
  if (min < 60) return `${min.toFixed(0)} min`
  if (min < 1440) return `${(min / 60).toFixed(1)} h`
  return `${(min / 1440).toFixed(1)} d`
}

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'brand',
}: {
  label: string
  value: number | string
  sub?: string
  icon: React.ElementType
  color?: 'brand' | 'purple' | 'amber' | 'green' | 'red'
}) {
  const colorClasses = {
    brand: 'bg-brand-50 text-brand-700',
    purple: 'bg-purple-50 text-purple-700',
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
  }
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <div className="text-3xl font-bold text-gray-900">
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        <div className="text-sm text-gray-500">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

function Panel({ title, icon: Icon, children }: { title: string; icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5">
      <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}
        {title}
      </h3>
      {children}
    </div>
  )
}

function BarChart({ data, label, color = 'bg-brand-400' }: { data: DailyCount[]; label: string; color?: string }) {
  const max = Math.max(1, ...data.map((d) => d.count))
  const total = data.reduce((s, d) => s + d.count, 0)
  const peak = data.reduce((m, d) => (d.count > m.count ? d : m), { date: '', count: 0 })
  const labelEvery = data.length > 31 ? Math.ceil(data.length / 15) : 1

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-semibold text-gray-800">{label}</h3>
        <span className="text-xs text-gray-500">
          {total.toLocaleString()} total{peak.count > 0 && ` · peak ${peak.count} on ${peak.date.slice(5)}`}
        </span>
      </div>
      <div className="flex items-end gap-1 overflow-x-auto pb-1">
        {data.map((d, i) => (
          <div key={d.date} className="flex flex-col items-center gap-1 min-w-[1.5rem] flex-1">
            <div className="flex items-end justify-center w-full h-40">
              <div
                className={`w-full max-w-[1.5rem] ${color} rounded-t transition-all hover:opacity-80`}
                style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? 3 : 0 }}
                title={`${d.date}: ${d.count}`}
              />
            </div>
            <span className="text-[10px] text-gray-400 h-3">{i % labelEvery === 0 ? d.date.slice(5) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function HBars({ items, color = 'bg-brand-500' }: { items: { label: string; count: number }[]; color?: string }) {
  const max = Math.max(1, ...items.map((i) => i.count))
  if (items.length === 0) return <p className="text-sm text-gray-400">No data for this selection.</p>
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-2 text-sm">
          <span className="w-40 truncate text-gray-700" title={it.label}>
            {it.label}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-3">
            <div className={`${color} h-3 rounded-full`} style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right text-gray-500">{it.count.toLocaleString()}</span>
        </div>
      ))}
    </div>
  )
}

function Funnel({ usage, visitors }: { usage: UsageStats; visitors: number }) {
  const stages = [
    { label: 'Unique visitors', value: visitors, color: 'bg-gray-400' },
    { label: 'Checklists started', value: usage.funnel.started, color: 'bg-brand-400' },
    { label: 'Submitted', value: usage.funnel.submitted, color: 'bg-brand-600' },
    { label: 'Published', value: usage.funnel.published, color: 'bg-green-600' },
  ]
  const max = Math.max(1, ...stages.map((s) => s.value))
  return (
    <div className="space-y-3">
      {stages.map((s, i) => {
        const prev = i > 0 ? stages[i - 1].value : null
        const conv = prev && prev > 0 ? (s.value / prev) * 100 : null
        return (
          <div key={s.label} className="flex items-center gap-3 text-sm">
            <span className="w-36 text-gray-600">{s.label}</span>
            <div className="flex-1 bg-gray-100 rounded-md h-7 relative">
              <div
                className={`${s.color} h-7 rounded-md flex items-center px-2 text-white text-xs font-medium`}
                style={{ width: `${Math.max((s.value / max) * 100, s.value > 0 ? 6 : 0)}%` }}
              >
                {s.value.toLocaleString()}
              </div>
            </div>
            <span className="w-16 text-right text-xs text-gray-400">{conv != null ? `${conv.toFixed(0)}%` : ''}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function UsageTab() {
  const [preset, setPreset] = useState<Preset>('30d')
  const [customStart, setCustomStart] = useState(() => presetRange('30d').start)
  const [customEnd, setCustomEnd] = useState(() => presetRange('30d').end)

  const range = useMemo(
    () => (preset === 'custom' ? { start: customStart, end: customEnd } : presetRange(preset)),
    [preset, customStart, customEnd]
  )

  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['admin', 'stats', 'usage', range.start, range.end],
    queryFn: () => adminApi.getUsageStats(range),
  })
  const { data: traffic, isLoading: trafficLoading } = useQuery({
    queryKey: ['admin', 'stats', 'traffic', range.start, range.end],
    queryFn: () => adminApi.getTrafficStats(range),
  })

  const presets: { key: Preset; label: string }[] = [
    { key: '1d', label: 'Last day' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: 'custom', label: 'Custom' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Activity className="w-5 h-5 text-brand-600" />
          Usage &amp; traffic
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border bg-white p-0.5">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  preset === p.key ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={customStart}
                max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <span className="text-gray-400">→</span>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={isoDay(new Date())}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          )}
        </div>
      </div>

      {usage && (
        <p className="text-sm text-gray-500 -mt-2">
          Showing {usage.range.start} → {usage.range.end} ({usage.range.days} day
          {usage.range.days === 1 ? '' : 's'})
        </p>
      )}

      {usageLoading || trafficLoading ? (
        <div className="p-8 text-center text-gray-500">Loading usage data…</div>
      ) : !usage || !traffic ? (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Could not load usage statistics.
        </div>
      ) : (
        <>
          {/* Engagement cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Submissions in period"
              value={usage.period.submissions}
              sub={`${usage.submissions.total.toLocaleString()} all-time`}
              icon={FileCheck}
            />
            <StatCard
              label="Published in period"
              value={usage.period.published}
              sub={`${usage.submissions.published.toLocaleString()} all-time`}
              icon={FileCheck}
              color="green"
            />
            <StatCard
              label="AI submissions in period"
              value={usage.period.ai}
              sub={`${usage.by_source.ai.toLocaleString()} all-time`}
              icon={Bot}
              color="purple"
            />
            <StatCard
              label="Report downloads"
              value={traffic.report_downloads.total}
              sub={`${traffic.report_downloads.pdf} PDF · ${traffic.report_downloads.html} HTML`}
              icon={Download}
              color="amber"
            />
          </div>

          {/* Funnel + completion */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Panel title="Conversion funnel" icon={Filter}>
                <Funnel usage={usage} visitors={traffic.unique_ips} />
              </Panel>
            </div>
            <Panel title="Completion time" icon={Clock}>
              <div className="space-y-3">
                <div>
                  <div className="text-3xl font-bold text-gray-900">{fmtMinutes(usage.completion.median_minutes)}</div>
                  <div className="text-sm text-gray-500">median time to complete</div>
                </div>
                <div className="text-sm text-gray-500">
                  Average {fmtMinutes(usage.completion.avg_minutes)} · {usage.completion.count} checklist
                  {usage.completion.count === 1 ? '' : 's'} with timing data
                </div>
              </div>
            </Panel>
          </div>

          {/* Trend charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarChart data={usage.submissions_per_day} label="Submissions per day" />
            <BarChart data={traffic.visitors_per_day} label="Unique visitors per day" color="bg-amber-400" />
          </div>

          {/* Traffic health */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <StatCard label="Requests" value={traffic.total_requests} sub={`${traffic.bot_requests} from bots`} icon={Users} />
            <StatCard label="Unique visitors" value={traffic.unique_ips} sub="excludes bots" icon={Users} color="green" />
            <StatCard label="Human requests" value={traffic.human_requests} sub={`${traffic.total_requests} incl. bots`} icon={Users} color="brand" />
            <StatCard
              label="Error rate"
              value={`${(traffic.error_rate * 100).toFixed(1)}%`}
              sub={`${(traffic.status_breakdown['4xx'] || 0) + (traffic.status_breakdown['5xx'] || 0)} of ${traffic.total_requests}`}
              icon={AlertCircle}
              color={traffic.error_rate > 0.05 ? 'red' : 'green'}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="HTTP status breakdown" icon={Activity}>
              <div className="grid grid-cols-4 gap-3">
                {(['2xx', '3xx', '4xx', '5xx'] as const).map((k) => {
                  const v = traffic.status_breakdown[k] || 0
                  const tone =
                    k === '2xx' ? 'text-green-700' : k === '3xx' ? 'text-gray-700' : k === '4xx' ? 'text-amber-700' : 'text-red-700'
                  return (
                    <div key={k} className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className={`text-xl font-bold ${tone}`}>{v.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{k}</div>
                    </div>
                  )
                })}
              </div>
            </Panel>
            <Panel title="Top errors (4xx / 5xx)" icon={AlertCircle}>
              {traffic.top_errors.length === 0 ? (
                <p className="text-sm text-gray-400">No errors in this period. 🎉</p>
              ) : (
                <table className="min-w-full text-sm">
                  <tbody className="divide-y">
                    {traffic.top_errors.map((e) => (
                      <tr key={`${e.path}-${e.status}`}>
                        <td className="py-1.5 pr-2">
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                              e.status >= 500 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {e.status}
                          </span>
                        </td>
                        <td className="py-1.5 pr-2 font-mono text-gray-700 truncate max-w-[18rem]" title={e.path}>
                          {e.path}
                        </td>
                        <td className="py-1.5 text-right text-gray-900">{e.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          {/* AI insights */}
          <Panel title="AI extraction insights (jobs started in period)" icon={Bot}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-gray-900">{usage.ai.jobs}</div>
                <div className="text-xs text-gray-500">Jobs</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-green-700">
                  {usage.ai.success_rate != null ? `${(usage.ai.success_rate * 100).toFixed(0)}%` : '—'}
                </div>
                <div className="text-xs text-gray-500">Success rate</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-gray-900">{fmtDuration(usage.ai.avg_duration_sec)}</div>
                <div className="text-xs text-gray-500">Avg duration</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-gray-900">{usage.ai.avg_fields ?? '—'}</div>
                <div className="text-xs text-gray-500">Avg fields</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-red-700">{usage.ai.failed}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
            </div>
            {usage.ai.by_provider.length > 0 && (
              <HBars
                items={usage.ai.by_provider.map((p) => ({ label: p.provider, count: p.count }))}
                color="bg-purple-500"
              />
            )}
          </Panel>

          {/* Content insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Top EV sources (all submitted)">
              <HBars items={usage.content.ev_sources.map((s) => ({ label: s.value, count: s.count }))} />
            </Panel>
            <Panel title="Top EV applications (all submitted)">
              <HBars
                items={usage.content.ev_applications.map((s) => ({ label: s.value, count: s.count }))}
                color="bg-green-500"
              />
            </Panel>
          </div>

          {/* Referrers + top paths */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Top referrers" icon={Globe}>
              <HBars
                items={traffic.top_referrers.map((r) => ({ label: r.referer, count: r.count }))}
                color="bg-brand-400"
              />
            </Panel>
            <Panel title="Top requested paths" icon={Users}>
              {traffic.top_paths.length === 0 ? (
                <p className="text-sm text-gray-400">No traffic data for this period.</p>
              ) : (
                <HBars items={traffic.top_paths.map((p) => ({ label: p.path, count: p.count }))} />
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  )
}
